import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatbotActiveFilters, ChatbotMessage } from '@/models/chatbot';
import { groupConversationsByDate, ChatHistoryGroup } from '../ChatHistory/groupConversationsByDate';
import { HistoryTimeLabels } from '../ChatHistory/formatHistoryTime';
import useChatbotStore from '../store/useChatbotStore';
import { getHistoryErrorMessageKey } from './historyError';
import useConversationList from './useConversationList';
import useConversationMutations from './useConversationMutations';
import useOpenConversation from './useOpenConversation';
import useRenameConversation from './useRenameConversation';
import useDeleteConversation from './useDeleteConversation';

interface UseChatHistoryPanelParams {
  isOpen: boolean;
  onConversationLoaded: (
    conversationId: string,
    messages: ChatbotMessage[],
    pinnedFilters: ChatbotActiveFilters | null,
  ) => void;
}

const useChatHistoryPanel = ({ isOpen, onConversationLoaded }: UseChatHistoryPanelParams) => {
  const { t } = useTranslation();
  const activeConversationId = useChatbotStore(s => s.activeConversationId);

  const [searchTerm, setSearchTerm] = useState('');

  const {
    conversations,
    isLoading,
    isError,
    error,
    isFetchNextPageError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversationList({
    searchTerm,
    enabled: isOpen,
  });
  const { rename, remove, commitRename } = useConversationMutations();
  const { openConversation, retryOpen, clearOpenError, openingId, openError } = useOpenConversation({
    onConversationLoaded,
  });

  const renameConversation = useRenameConversation({ rename, commitRename });
  const deleteConversation = useDeleteConversation({ remove });

  const [isLoadErrorDismissed, setIsLoadErrorDismissed] = useState(false);

  const retryLoad = useCallback((): void => {
    setIsLoadErrorDismissed(false);
    void refetch();
  }, [refetch]);

  const dismissLoadError = useCallback((): void => {
    setIsLoadErrorDismissed(true);
  }, []);

  const retryLoadMore = useCallback((): void => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  useEffect(() => {
    if (isOpen) {
      clearOpenError();
      setIsLoadErrorDismissed(false);
    }
  }, [clearOpenError, searchTerm, isOpen]);

  const groups = useMemo<ChatHistoryGroup[]>(() => groupConversationsByDate(conversations), [conversations]);

  const loadErrorMessage = t(getHistoryErrorMessageKey('load', error));
  const loadMoreErrorMessage = t(getHistoryErrorMessageKey('loadMore', error));

  const hasResults = conversations.length > 0;
  const isSearching = searchTerm.trim().length > 0;
  const showEmptyState = isOpen && !isLoading && !isError && !hasResults && !isFetchingNextPage;
  const isSearchDisabled = isLoading || (!isSearching && !hasResults);
  const emptyTitle = isSearching
    ? t('components.aiChatDrawer.history.noResultsTitle')
    : t('components.aiChatDrawer.history.emptyTitle');
  const emptySubtitle = isSearching
    ? t('components.aiChatDrawer.history.noResults')
    : t('components.aiChatDrawer.history.emptySubtitle');

  const timeLabels = useMemo<HistoryTimeLabels>(
    () => ({
      minAgo: count => t('components.aiChatDrawer.history.relativeTime.minAgo', { count }),
      hrAgo: count =>
        count === 1
          ? t('components.aiChatDrawer.history.relativeTime.hrAgo', { count })
          : t('components.aiChatDrawer.history.relativeTime.hrsAgo', { count }),
      yesterday: t('components.aiChatDrawer.history.groups.yesterday'),
    }),
    [t],
  );

  return {
    activeConversationId,
    searchTerm,
    setSearchTerm,
    clearSearch,
    isSearchDisabled,
    isLoading,
    isError: isError && !isLoadErrorDismissed,
    loadErrorMessage,
    openError,
    isLoadMoreError: isFetchNextPageError,
    loadMoreErrorMessage,
    retryLoad,
    retryLoadMore,
    dismissLoadError,
    dismissOpenError: clearOpenError,
    showEmptyState,
    emptyTitle,
    emptySubtitle,
    groups,
    timeLabels,
    openingId,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    openConversation,
    retryOpen,
    ...renameConversation,
    ...deleteConversation,
  };
};

export default useChatHistoryPanel;
